import * as d3 from 'd3-selection'
import contentWidth from './content-width.js'

const column0 = (tree, transitions, enter, update) => {
  let enter0 = enter.filter(d => d.idx === 0)
                    .append('div')
                      .attr('class', 'node-contents')
                      .attr('style', d => {
                        let x = transitions ? (d.node.parent ? d.node.parent._x : 0) : d.node._x
                        return `${tree.prefix}transform: translate(${x}px, 0px)`
                      })

  enter0.append('div')
          .attr('class', d => {
            return 'toggler ' + (d.node.children ? 'expanded' : (d.node._allChildren && d.node._allChildren.length) ? 'collapsed' : 'leaf')
          })
          .on('click', (e, d) => tree._onToggle(e, d.node))
            .append('svg')
              .append('use')
              .attr('xlink:href', '#icon-collapsed')

  enter0.append('div')
        .attr('class', 'label')
        .call(selection => {
          selection.each(function (d) {
            d.renderer(this)
          })
        })

  let contents = update.filter(d => d.idx === 0)
                       .attr('data-x-offset', d => d.node._x) // Store the x-offset, so we can include it in dynamic width calculations
                       .selectAll('.node-contents')
                       .data(d => [d])

  contents.selectAll('.label')

  contents.select('.toggler')
          .attr('class', d => {
            return 'toggler ' + (d.node.children ? 'expanded' : (d.node._allChildren && d.node._allChildren.length) ? 'collapsed' : 'leaf')
          })
          .on('mouseup', function (e) {
            tree._onToggle.bind(tree)
            e.stopPropagation()
          })
}

const select = (el, e, d, tree, opts = {}) => {
  if (tree._filteredResults) {
    // We're showing search results. kalpa-tree clears seach results anytime
    // a node is selected, but we don't want that behavior for the record-list

    // Override tree internals.

    // Remove whatever was previously selected
    tree._deselect()

    // Mark this node as selected
    d.node.selected = true
    tree._selected = d.node
    tree.node.classed('selected', d => d.selected)

    // Emit normal select events
    tree.emit('select', tree.nodes[d.id])
    tree.emit('selected')
  } else {
    opts = Object.assign({}, tree.options, opts)
    // Run normal tree behavior
    tree._onSelect(e, d.node, tree.options)
  }
}

class Contents {

  constructor (config) {
    this.config = config
    this.render = this.render.bind(this)
  }

  render (selection, tree, transitions) {
    let config = this.config

    selection.each(function (data) {
      let node = d3.select(this)
                   .on('click', (e) => {
                     // Disable tree click event handling, we handle clicks
                     // on each column
                     e.preventDefault()
                   })
        , columns = node.selectAll('.column')
                        .data((d, i) => {
                          if (!d._columnWidths) {
                            // Initialize the column widths for this row, these will be populated
                            // when columns are rendered
                            d._columnWidths = []
                          }

                          let columns = tree.get(d.id).columns.map((column, i) => {
                            return {
                              column,
                              idx: i,
                              _width: column?._width, // From previous renderers
                              clazz: config.editableColumns.includes(i) ? 'column-editable' : '',
                              renderer: el => {
                                config.columns[i].render(el, column, tree.get(d.id), i)
                                d._columnWidths[config.rowAction ? i + 1 : i] = contentWidth(el)
                              },
                              node: d
                            }
                          })

                          if (config.rowAction) {
                            // We want to add another column for the row action
                            // element
                            columns.unshift({
                              action: true,
                              clazz: 'row-action-column',
                              renderer: el => {
                                config.rowAction(el, tree.get(d.id))
                                d._columnWidths[0] = contentWidth(el)
                              },
                              node: d
                            })
                          }

                          return columns
                        })
        , enter = columns.enter()
                         .append('div')
                         .attr('class', d => {
                           const clazzes = ['column', d.clazz]
                           if (d.idx !== undefined) {
                             clazzes.push(`column-${d.idx}`)
                           }
                           return clazzes.join(' ')
                         })
                         .on('mousedown', function (e, d) {
                           // Stores a boolean value if the mousedown event
                           // was fired on the control element of an editable column
                           this.parentNode._controlMouseDown = d3.select(this).classed('column-editable') &&
                             !(e.target == this)
                         })
                         .on('mouseup', function (e, d) {
                           if (this.parentNode._controlMouseDown) {
                             // They started this click on an editable column internal contents
                             // We want to select the row, but never toggle it.
                             // See https://github.com/SpiderStrategies/Scoreboard/issues/36444
                             select(this, e, d, tree, { toggleOnSelect: false })
                           } else {
                             // Simple case. They didn't click the "control" element
                             // within an editable column. Run normal tree select behavior
                             select(this, e, d, tree)
                           }
                         })

      // The first column is more advanced since it may require a toggler
      column0(tree, transitions, enter, columns)

      // Remaining columns
      enter.filter(d => d.idx !== 0)
           .call(selection => {
             selection.each(function (d) {
               d.renderer(this)
             })
           })
    })
  }

}

export default Contents
